import { BohemiaCodeLoader } from '@ms/bohemia-codeloader';
import { deserializeError, sendErrorEvent } from '@fluidx/utilities';
import { ITelemetryNamespaceLogger, ContainerEnvironment } from '@fluidx/office-fluid-types';
import { DefaultProductionIframeUrl, PreloadPostMessageHandlerId } from './Constants';
import { setupPostMessageReceiver } from './Iframe/Shared/PostMessage';
import {
  setIframeModeQueryParameter,
  setParentOriginQueryParameter,
  setSkipSwCacheParam
} from './Iframe/Shared/QueryParams';
import { PreloadErrorMessage } from './Iframe/MessageTypes';
import { createHiddenIframe } from './createHiddenIframe';
import { OWHErrorEvent } from './Telemetry';
import { rerouteIframeToSourcelessIframe } from './Iframe-sourceless/rerouteIframeToSourcelessIframe';

function preloadStaticContentInIframe(): Promise<void> {
  const iframeUrl = new URL(DefaultProductionIframeUrl);
  // We set a 'mode' query parameter to preload the iframe know that it should be in preload mode rather than in component load mode
  setIframeModeQueryParameter(iframeUrl, 'preload');
  setParentOriginQueryParameter(iframeUrl, location.origin);
  // We want to use the latest version of the iframe page for the preload flow, set a query parameter to ensure we don't load the index.html from a cache
  setSkipSwCacheParam(iframeUrl, true);

  // We create an iframe with some styles to make it hidden in order to load the service worker and relevant static content
  const iframe = createHiddenIframe(iframeUrl);

  return new Promise((resolve, reject) => {
    const postMessageReceiver = setupPostMessageReceiver(
      window,
      iframeUrl.origin,
      {
        ['PreloadDone']: (_) => {
          postMessageReceiver.remove();
          document.body.removeChild(iframe);
          resolve();
        },
        ['PreloadError']: (m) => {
          const message = m as PreloadErrorMessage;
          postMessageReceiver.remove();
          document.body.removeChild(iframe);

          reject(deserializeError(message.payload.error));
        }
      },
      PreloadPostMessageHandlerId
    );

    document.body.appendChild(iframe);
  });
}

async function preloadStaticContentInCurrentContext(
  containerEnvironment: ContainerEnvironment | undefined,
  locale?: string
) {
  await new BohemiaCodeLoader({
    containerEnvironment: containerEnvironment || ContainerEnvironment.Prod,
    localeHint: locale
  }).preCache('@ms/office-fluid-container');
}

export interface OfficeWebHostPreloadConfig {
  /**
   * The environment in which the container is being loaded, defaults to production
   */
  containerEnvironment?: ContainerEnvironment;

  /**
   * Logger used to collect additional info about the container/component load process
   * and office-web-host logs.
   */
  logger?: ITelemetryNamespaceLogger;

  /**
   * Set if the application will be loading fluid content in a regular (sourced) iframe,
   * so the relevant pieces can be prefetched.
   * For a sourceless iframe, you do *NOT* need to specify this config.
   */
  isolationConfig?: { useIframe: boolean };

  /**
   * The locale to use for UI such as strings. This enables us to preload only the relevant strings for that language.
   */
  locale?: string;
}

/**
 * The preload API is meant to be called by the host in situations where they determine it is likely
 * that fluid content will be created or viewed. This currently does things like prefetch code that will be used
 * and install a service worker for the regular (sourced) iframe scenario.
 **/
export async function preload(config: OfficeWebHostPreloadConfig): Promise<void> {
  const { logger, containerEnvironment, isolationConfig, locale } = config;

  try {
    if (isolationConfig && isolationConfig.useIframe && !rerouteIframeToSourcelessIframe()) {
      // Sourced iframes currently do not support preloading localized assets.
      return preloadStaticContentInIframe();
    }

    // Otherwise, preload in the current context:
    return preloadStaticContentInCurrentContext(containerEnvironment, locale);
  } catch (e) {
    if (logger) {
      sendErrorEvent(logger, { eventName: OWHErrorEvent.PreloadError }, e);
    }
  }
}
